import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import qs from "qs";
import { getApiErrorMessage } from "../../helpers/apiNotify";
import { downloadUserAuditCsv } from "../../helpers/userAuditDownload";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

const authConfig = (accessToken) => ({
  headers: {
    "ngrok-skip-browser-warning": "69420",
    "Content-Type": "application/json",
    Authorization: `${accessToken}`,
  },
});

const cleanParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      out[key] = value;
    }
  });
  return out;
};

/** GET /user-audit-logs — paged global log */
export const fetchUserAuditLogs = createAsyncThunk(
  "userAudit/fetchLogs",
  async ({ accessToken, ...params }, { rejectWithValue }) => {
    try {
      const response = await API.get(
        `/user-audit-logs?${qs.stringify(cleanParams(params))}`,
        authConfig(accessToken)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to retrieve audit logs")
      );
    }
  }
);

/** GET /users/:userId/audit-trail — History for one user */
export const fetchUserAuditTrail = createAsyncThunk(
  "userAudit/fetchUserTrail",
  async ({ accessToken, userId, ...params }, { rejectWithValue }) => {
    try {
      const response = await API.get(
        `/users/${userId}/audit-trail?${qs.stringify(cleanParams(params))}`,
        authConfig(accessToken)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to retrieve audit trail")
      );
    }
  }
);

/** GET /user-audit-logs/verify — Administrator only */
export const verifyUserAuditChain = createAsyncThunk(
  "userAudit/verify",
  async ({ accessToken }, { rejectWithValue }) => {
    try {
      const response = await API.get(
        `/user-audit-logs/verify`,
        authConfig(accessToken)
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to verify the audit log")
      );
    }
  }
);

export const exportUserAuditLogs = async ({ accessToken, ...params }) =>
  downloadUserAuditCsv({
    path: "/user-audit-logs/export",
    accessToken,
    query: cleanParams(params),
    fileName: "user_audit_log",
  });

export const exportUserAuditTrail = async ({
  accessToken,
  userId,
  ...params
}) =>
  downloadUserAuditCsv({
    path: `/users/${userId}/audit-trail/export`,
    accessToken,
    query: cleanParams(params),
    fileName: `user_${userId}_audit_trail`,
  });

const emptyList = {
  logs: [],
  loading: false,
  error: null,
  pagination: { total: 0, currentPage: 1, totalPages: 1 },
};

const initialState = {
  global: { ...emptyList },
  userTrail: { ...emptyList, userId: null },
  verify: {
    loading: false,
    error: null,
    result: null,
  },
};

const userAuditSlice = createSlice({
  name: "userAudit",
  initialState,
  reducers: {
    clearUserAuditLogs(state) {
      state.global = { ...emptyList };
    },
    clearUserAuditTrail(state) {
      state.userTrail = { ...emptyList, userId: null };
    },
    clearVerifyResult(state) {
      state.verify = { loading: false, error: null, result: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserAuditLogs.pending, (state) => {
        state.global.loading = true;
        state.global.error = null;
      })
      .addCase(fetchUserAuditLogs.fulfilled, (state, action) => {
        state.global.loading = false;
        state.global.logs = action.payload?.logs || [];
        state.global.pagination = {
          total: action.payload?.total ?? 0,
          currentPage: action.payload?.currentPage ?? 1,
          totalPages: action.payload?.totalPages ?? 1,
        };
      })
      .addCase(fetchUserAuditLogs.rejected, (state, action) => {
        state.global.loading = false;
        state.global.error = action.payload || action.error.message;
        state.global.logs = [];
      })
      .addCase(fetchUserAuditTrail.pending, (state) => {
        state.userTrail.loading = true;
        state.userTrail.error = null;
      })
      .addCase(fetchUserAuditTrail.fulfilled, (state, action) => {
        state.userTrail.loading = false;
        state.userTrail.logs = action.payload?.logs || [];
        state.userTrail.pagination = {
          total: action.payload?.total ?? 0,
          currentPage: action.payload?.currentPage ?? 1,
          totalPages: action.payload?.totalPages ?? 1,
        };
      })
      .addCase(fetchUserAuditTrail.rejected, (state, action) => {
        state.userTrail.loading = false;
        state.userTrail.error = action.payload || action.error.message;
        state.userTrail.logs = [];
      })
      .addCase(verifyUserAuditChain.pending, (state) => {
        state.verify.loading = true;
        state.verify.error = null;
      })
      .addCase(verifyUserAuditChain.fulfilled, (state, action) => {
        state.verify.loading = false;
        state.verify.result = action.payload;
      })
      .addCase(verifyUserAuditChain.rejected, (state, action) => {
        state.verify.loading = false;
        state.verify.error = action.payload || action.error.message;
        state.verify.result = null;
      });
  },
});

export const { clearUserAuditLogs, clearUserAuditTrail, clearVerifyResult } =
  userAuditSlice.actions;
export default userAuditSlice.reducer;
